import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowRight, Send, Bot, User, Book, Loader2, 
  Plus, MessageSquare, Trash2, ChevronLeft
} from 'lucide-react';
import { Logo } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LegalAI() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSidebar, setShowSidebar] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
    fetchSessions();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/legal-library/ai/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSessions(response.data);
    } catch (err) {
      console.error('Error fetching sessions:', err);
    }
  };

  const loadSession = async (sid) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/legal-library/ai/history/${sid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(response.data);
      setSessionId(sid);
    } catch (err) {
      console.error('Error loading session:', err);
    }
  };

  const startNewSession = () => {
    setSessionId(null);
    setMessages([]);
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm('هل أنت متأكد من حذف هذه المحادثة؟')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/legal-library/ai/sessions/${sid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchSessions();
      if (sessionId === sid) {
        startNewSession();
      }
    } catch (err) {
      console.error('Error deleting session:', err);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: inputMessage,
      created_at: new Date().toISOString(),
    };
    
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/legal-library/ai/chat`,
        {
          message: inputMessage,
          session_id: sessionId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.data.response,
        sources: response.data.sources,
        created_at: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
      
      if (!sessionId) {
        setSessionId(response.data.session_id);
        fetchSessions();
      }
    } catch (err) {
      console.error('Error sending message:', err);
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'عذراً، حدث خطأ أثناء معالجة طلبك. يرجى المحاولة مرة أخرى.',
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const suggestedQuestions = [
    'ما هي إجراءات رفع دعوى عمالية؟',
    'ما هي حقوق المستأجر في عقد الإيجار؟',
    'ما هي شروط الحضانة في النظام السعودي؟',
    'ما هي عقوبة الاحتيال المالي؟',
  ];

  return (
    <div className="min-h-screen bg-sand flex" dir="rtl" lang="ar">
      {/* الشريط الجانبي */}
      <aside className={`${showSidebar ? 'w-72' : 'w-0'} bg-primary transition-all duration-300 overflow-hidden flex flex-col`}>
        <div className="p-4 border-b border-white/10">
          <button
            onClick={startNewSession}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gold text-white rounded-lg hover:bg-gold-dark transition-all"
            data-testid="new-chat-btn"
          >
            <Plus className="w-5 h-5" />
            محادثة جديدة
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          <p className="text-white/60 text-sm font-body mb-2">المحادثات السابقة</p>
          {sessions.length === 0 ? (
            <p className="text-white/40 text-sm font-body text-center py-4">
              لا توجد محادثات سابقة
            </p>
          ) : (
            sessions.map((session) => (
              <button
                key={session._id}
                onClick={() => loadSession(session._id)}
                className={`w-full flex items-center justify-between p-3 rounded-lg transition-all group ${
                  sessionId === session._id
                    ? 'bg-white/20 text-white'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <MessageSquare className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm font-body truncate">
                    {session.first_message?.substring(0, 30)}...
                  </span>
                </div>
                <button
                  onClick={(e) => deleteSession(session._id, e)}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 rounded"
                >
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </button>
            ))
          )}
        </div>

        <div className="p-4 border-t border-white/10">
          <button
            onClick={() => navigate('/legal-library')}
            className="w-full flex items-center gap-2 px-4 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-all"
          >
            <Book className="w-5 h-5" />
            العودة للمكتبة
          </button>
        </div>
      </aside>

      {/* المحتوى الرئيسي */}
      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-gray-100 py-4 px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-all"
            >
              <ChevronLeft className={`w-5 h-5 transition-transform ${showSidebar ? '' : 'rotate-180'}`} />
            </button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Bot className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h1 className="text-lg font-heading font-bold text-primary">المساعد القانوني الذكي</h1>
                <p className="text-sm text-gray-500 font-body">مدعوم بالذكاء الاصطناعي Gemini</p>
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-600 hover:text-primary transition-all"
          >
            <ArrowRight className="w-5 h-5" />
            العودة
          </button>
        </header>

        {/* منطقة الرسائل */}
        <div className="flex-1 overflow-y-auto p-6">
          {messages.length === 0 ? (
            <div className="max-w-2xl mx-auto text-center py-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Bot className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-primary mb-4">
                مرحباً بك في المساعد القانوني الذكي
              </h2>
              <p className="text-gray-600 font-body mb-8">
                يمكنني مساعدتك في الإجابة على الأسئلة القانونية المتعلقة بالأنظمة السعودية والفقه الإسلامي
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {suggestedQuestions.map((question, idx) => (
                  <button
                    key={idx}
                    onClick={() => setInputMessage(question)}
                    className="p-4 bg-white rounded-xl border border-gray-200 text-right hover:border-gold hover:shadow-md transition-all"
                    data-testid={`suggested-question-${idx}`}
                  >
                    <p className="text-sm text-gray-700 font-body">{question}</p>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto space-y-6">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex gap-4 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.role === 'user' ? 'bg-gold' : 'bg-green-100'
                  }`}>
                    {msg.role === 'user' ? (
                      <User className="w-5 h-5 text-white" />
                    ) : (
                      <Bot className="w-5 h-5 text-green-600" />
                    )}
                  </div>
                  <div className={`flex-1 max-w-[80%] ${msg.role === 'user' ? 'text-right' : ''}`}>
                    <div className={`p-4 rounded-2xl ${
                      msg.role === 'user'
                        ? 'bg-gold text-white rounded-tr-none'
                        : 'bg-white border border-gray-100 rounded-tl-none'
                    }`}>
                      <p className="font-body whitespace-pre-wrap">{msg.content}</p>
                    </div>
                    
                    {msg.sources && msg.sources.length > 0 && (
                      <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                        <p className="text-xs text-blue-600 font-body mb-2">المصادر المستخدمة:</p>
                        <div className="space-y-1">
                          {msg.sources.map((source, idx) => (
                            <p key={idx} className="text-xs text-blue-800 font-body">
                              • {source.title} {source.year && `(${source.year})`}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {loading && (
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="p-4 bg-white border border-gray-100 rounded-2xl rounded-tl-none">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 text-green-600 animate-spin" />
                      <span className="text-gray-500 font-body">جاري التفكير...</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* حقل الإدخال */}
        <div className="bg-white border-t border-gray-100 p-4">
          <form onSubmit={sendMessage} className="max-w-4xl mx-auto flex gap-4">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="اكتب سؤالك القانوني هنا..."
              className="flex-1 px-6 py-4 bg-gray-50 border border-gray-200 rounded-xl font-body focus:ring-2 focus:ring-gold focus:border-transparent"
              disabled={loading}
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={loading || !inputMessage.trim()}
              className="px-6 py-4 bg-gold text-white rounded-xl hover:bg-gold-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="send-message-btn"
            >
              <Send className="w-6 h-6" />
            </button>
          </form>
          <p className="text-center text-xs text-gray-400 font-body mt-3">
            الإجابات للاسترشاد فقط وليست بديلاً عن الاستشارة القانونية المتخصصة
          </p>
        </div>
      </div>
    </div>
  );
}
